<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useMenu, ZMenu, ZMenuItem, ZAvatar } from '@zoomcatalog/core-ui';
import type { AvatarSize } from '@zoomcatalog/core-ui/dist/components/avatar/types';
import { useAssetsService } from '@zoomcatalog/design-system';
import { useCompanyStore } from '@/composable/store-utils/useCompanyStore';
import { useProjectStore } from '@/composable/store-utils/useProjectStore';
import { useCompanyQuery } from '@/service/request-handlers/useCompanyService';
import { useUserQuery } from '@/service/request-handlers/useUser';
import {
  UserCompany,
  useUserCompaniesQuery,
} from '@/service/request-handlers/useUserCompanies';
import {
  UserDistributorCompany,
  useUserDistributorCompaniesQuery,
} from '@/service/request-handlers/useUserDistributorCompanies';

const locale = {
  SWITCH_COMPANIES: 'Switch companies',
};

const { projectData } = useProjectStore();
const companyStore = useCompanyStore();
const assetService = useAssetsService();
const [companySwitcherMenuProps, companySwitcherMenuHandlers] = useMenu();
const companySwitcherStyles = {
  width: '224px',
  maxHeight: '60vh',
};
useCompanyQuery({
  options: reactive({
    companyId: computed(() => projectData.value.company_id),
  }),
  handlers: {
    onSuccess(company) {
      companyStore.setCompany(company);
    },
  },
});
useUserCompaniesQuery({
  options: reactive({
    companyId: computed(() => projectData.value.company_id),
  }),
  handlers: {
    onSuccess(userCompanies) {
      companyStore.setUserCompanies(
        userCompanies.filter(
          (userCompany) =>
            userCompany.company_id === projectData.value.company_id
        )
      );
    },
  },
});
const userQuery = useUserQuery();
useUserDistributorCompaniesQuery({
  options: reactive({
    userId: computed(() => userQuery.data.value?.uid),
  }),
  handlers: {
    onSuccess(userDistributorCompanies) {
      companyStore.setDistributorCompanies(userDistributorCompanies);
    },
  },
});

function selectCompany(companyId: string) {
  companyStore.setSelectedCompanyId(companyId);
  companySwitcherMenuHandlers.closeMenu();
}

function getCompanyColor(color: string | undefined) {
  if (!color || color.length < 3) return;

  return color;
}

function getAvatarProps(
  company: UserCompany | UserDistributorCompany | undefined
) {
  return {
    size: 'xs' as AvatarSize,
    text: company?.name,
    bgColor: getCompanyColor(company?.brand?.primary_color),
  };
}
</script>

<template>
  <div v-if="companyStore.selectedCompany.value" class="company-switcher">
    <button
      class="company-switcher__button"
      type="button"
      @click="companySwitcherMenuHandlers.openMenu"
    >
      <ZAvatar v-bind="getAvatarProps(companyStore.selectedCompany.value)">
        <template
          v-if="companyStore.selectedCompany.value?.brand?.square_logo_url"
          #customImage
        >
          <img
            :src="
              assetService.getResizeUrl(
                companyStore.selectedCompany.value?.brand?.square_logo_url ??
                  '',
                ['100', '100']
              )
            "
          />
        </template>
      </ZAvatar>
      {{ companyStore.selectedCompany.value?.name }}
    </button>

    <ZMenu
      class="company-switcher__menu"
      v-bind="companySwitcherMenuProps"
      placement="bottom-start"
      :style="companySwitcherStyles"
      @close="companySwitcherMenuHandlers.closeMenu"
    >
      <template #header>
        <div class="company-switcher__menu__header">
          {{ locale.SWITCH_COMPANIES }}
        </div>
      </template>
      <ZMenuItem
        v-for="userCompany in companyStore.userCompanies.value"
        :key="userCompany.company_id"
        class="company-switcher__menu__item"
        button
        :rounded="false"
        @click="selectCompany(userCompany.company_id)"
      >
        <template #companyIcon>
          <ZAvatar v-bind="getAvatarProps(userCompany)">
            <template v-if="userCompany?.brand?.square_logo_url" #customImage>
              <img
                :src="
                  assetService.getResizeUrl(
                    userCompany?.brand?.square_logo_url ?? '',
                    ['100', '100']
                  )
                "
              />
            </template>
          </ZAvatar>
        </template>
        {{ userCompany.name }}
      </ZMenuItem>
      <ZMenuItem
        v-for="distributorCompany in companyStore.distributorCompanies.value"
        :key="distributorCompany.company_id"
        class="company-switcher__menu__item"
        button
        :rounded="false"
        @click="selectCompany(distributorCompany.company_id)"
      >
        <template #companyIcon>
          <ZAvatar v-bind="getAvatarProps(distributorCompany)">
            <template
              v-if="distributorCompany?.brand?.square_logo_url"
              #customImage
            >
              <img
                :src="
                  assetService.getResizeUrl(
                    distributorCompany?.brand?.square_logo_url ?? '',
                    ['100', '100']
                  )
                "
              />
            </template>
          </ZAvatar>
        </template>
        {{ distributorCompany.name }}
      </ZMenuItem>
    </ZMenu>
  </div>
</template>

<style lang="scss" scoped>
.company-switcher {
  &__button {
    @apply text-primary text-ui-md font-medium;
    @apply flex items-center gap-2;
  }
  &__menu {
    &__header {
      @apply text-ui-xs text-secondary font-medium;
    }
  }
}
</style>
